/**
 * Modal dialog for creating or editing a ConversationSignal.
 *
 * Handles both global signals and scoped signals (stream / user).
 * Strategy assignment uses ArrowUp/ArrowDown buttons for reordering.
 * Includes filter_colors inputs (dot, bg, text, border) with HTML color pickers.
 * Errors clear whenever the dialog opens fresh.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { signalsApi } from '@/api/conversationSignals';
import type {
  ConversationSignal,
  ConversationStrategy,
  SignalUpsertPayload,
} from '@/types/conversationSignals';

// ── Filter colors ─────────────────────────────────────────────────────────────

const COLOR_KEYS: Array<{ key: string; label: string }> = [
  { key: 'dot', label: 'Dot' },
  { key: 'bg', label: 'Background' },
  { key: 'text', label: 'Text' },
  { key: 'border', label: 'Border' },
];

interface ColorRowProps {
  colorKey: string;
  label: string;
  value: string;
  disabled: boolean;
  onChange: (key: string, value: string) => void;
}

function ColorRow({ colorKey, label, value, disabled, onChange }: ColorRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: 0.5,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: value || 'transparent',
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" sx={{ width: 80, flexShrink: 0 }}>
        {label}
      </Typography>
      {/* Native color picker */}
      <Box
        component="input"
        type="color"
        value={value || '#cccccc'}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(colorKey, e.target.value)}
        sx={{ width: 36, height: 32, p: 0, border: 'none', cursor: 'pointer', flexShrink: 0 }}
      />
      <TextField
        size="small"
        value={value}
        placeholder="#rrggbb"
        disabled={disabled}
        onChange={(e) => onChange(colorKey, e.target.value)}
        inputProps={{ maxLength: 9, style: { fontFamily: 'monospace', fontSize: 13 } }}
        sx={{ flex: 1 }}
      />
    </Box>
  );
}

// ── Form state ────────────────────────────────────────────────────────────────

interface SignalFormState {
  label: string;
  description: string;
  tone_guidance: string;
  ui_category: string;
  is_active: boolean;
  filter_colors: Record<string, string>;
}

function initForm(signal: ConversationSignal | null): SignalFormState {
  return {
    label: signal?.label ?? '',
    description: signal?.description ?? '',
    tone_guidance: signal?.tone_guidance ?? '',
    ui_category: signal?.ui_category ?? '',
    is_active: signal?.is_active ?? true,
    filter_colors: signal?.filter_colors ?? {},
  };
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  /** Signal to edit. null = create mode. */
  signal: ConversationSignal | null;
  /** All available strategies for selection. */
  allStrategies: ConversationStrategy[];
  /** Scope context. Omit or pass undefined for global signals. */
  scopeType?: 'stream' | 'user';
  scopeUuid?: string;
  onClose: () => void;
  onSaved: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

const SignalModal: React.FC<Props> = ({
  open,
  signal,
  allStrategies,
  scopeType,
  scopeUuid,
  onClose,
  onSaved,
}) => {
  const queryClient = useQueryClient();
  const isScoped = !!scopeType && !!scopeUuid;
  const isCreateMode = !signal;

  const [form, setForm] = useState<SignalFormState>(() => initForm(null));
  // Keys from the `allStrategies` list (not UUIDs — backend identifier is `key`)
  const [orderedKeys, setOrderedKeys] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [addStrategyInput, setAddStrategyInput] = useState<ConversationStrategy | null>(null);
  const [addStrategySearch, setAddStrategySearch] = useState<string>('');
  const [globalTemplateSignal, setGlobalTemplateSignal] = useState<ConversationSignal | null>(null);
  const [globalTemplateSearch, setGlobalTemplateSearch] = useState<string>('');

  const extractErrorMessage = (error: unknown): string => {
    const e = error as {
      response?: {
        data?: {
          error_messages?: string[];
          message?: string;
        };
      };
      message?: string;
    };
    return (
      e?.response?.data?.error_messages?.[0] ||
      e?.response?.data?.message ||
      e?.message ||
      'Failed to save changes. Please try again.'
    );
  };

  // Reset state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm(initForm(signal));
      setOrderedKeys((signal?.strategies ?? []).map((s) => s.key));
      setErrors({});
      setAddStrategyInput(null);
      setAddStrategySearch('');
      setGlobalTemplateSignal(null);
      setGlobalTemplateSearch('');
    }
  }, [open, signal]);

  const globalSignalsQuery = useQuery({
    queryKey: ['global-signal-templates'],
    queryFn: () => signalsApi.list({ max_results: 500 }),
    enabled: open && isScoped && isCreateMode,
  });

  const invalidate = () => {
    const queryKey = isScoped
      ? ['conversation-signals', 'scoped', scopeType, scopeUuid]
      : ['conversation-signals'];
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['conversation-signals'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: SignalUpsertPayload) => {
      if (isCreateMode) {
        const created = await signalsApi.create(payload);
        if (orderedKeys.length > 0) {
          await signalsApi.setStrategies(created.key, orderedKeys);
        }
        return created;
      }
      const updated = await signalsApi.update(signal!.key, payload);
      await signalsApi.setStrategies(updated.key, orderedKeys);
      return updated;
    },
    onSuccess: () => {
      invalidate();
      onSaved();
    },
  });

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    setOrderedKeys((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const handleMoveDown = (idx: number) => {
    setOrderedKeys((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const handleRemoveStrategy = (key: string) => {
    setOrderedKeys((prev) => prev.filter((k) => k !== key));
  };

  const handleAddStrategy = (strategy: ConversationStrategy | null) => {
    if (!strategy || orderedKeys.includes(strategy.key)) return;
    setOrderedKeys((prev) => [...prev, strategy.key]);
    setAddStrategyInput(null);
    setAddStrategySearch('');
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setForm((p) => ({
      ...p,
      filter_colors: { ...p.filter_colors, [colorKey]: value },
    }));
  };

  const getStrategy = (key: string) => allStrategies.find((s) => s.key === key);
  const availableStrategies = allStrategies.filter((s) => !orderedKeys.includes(s.key));
  const globalSignalTemplates = globalSignalsQuery.data?.results ?? [];

  const applyGlobalTemplate = (template: ConversationSignal | null) => {
    setGlobalTemplateSignal(template);
    if (!template) return;
    setForm({
      label: template.label,
      description: template.description ?? '',
      tone_guidance: template.tone_guidance ?? '',
      ui_category: template.ui_category ?? '',
      is_active: template.is_active,
      filter_colors: template.filter_colors ?? {},
    });
    setOrderedKeys((template.strategies ?? []).map((s) => s.key));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.label.trim()) e['label'] = 'Label is required';
    if (!form.description.trim()) e['description'] = 'Description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = () => {
    if (!validate()) return;
    const payload: SignalUpsertPayload = {
      label: form.label.trim(),
      description: form.description.trim(),
      tone_guidance: form.tone_guidance.trim() || undefined,
      ui_category: form.ui_category,
      is_active: form.is_active,
      filter_colors: form.filter_colors,
    };
    if (isScoped && isCreateMode) {
      payload.scope_type = scopeType;
      payload.scope_uuid = scopeUuid;
    }
    saveMutation.mutate(payload);
  };

  const isPending = saveMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {isCreateMode ? 'Create Signal' : 'Edit Signal'}
          </Typography>
          {isScoped && (
            <Chip label={scopeType} size="small" color="secondary" sx={{ fontSize: 11 }} />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" disabled={isPending}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5 }}>

        <TextField
          label="Label *"
          fullWidth
          size="small"
          value={form.label}
          onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))}
          error={!!errors['label']}
          helperText={
            errors['label'] ||
            (isCreateMode
              ? 'Unique label — also used as the AI enum value'
              : 'Label cannot be changed after creation')
          }
          disabled={isPending || !isCreateMode}
        />

        {isScoped && isCreateMode && (
          <Autocomplete
            size="small"
            options={globalSignalTemplates}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.key === v.key}
            value={globalTemplateSignal}
            inputValue={globalTemplateSearch}
            onInputChange={(_, value) => setGlobalTemplateSearch(value)}
            onChange={(_, value) => applyGlobalTemplate(value)}
            disabled={isPending || globalSignalTemplates.length === 0}
            loading={globalSignalsQuery.isLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Duplicate from global signal (optional)"
                placeholder="Select a global signal template…"
                helperText="Prefills all fields and strategies from the selected global signal."
              />
            )}
          />
        )}

        <TextField
          label="Description *"
          fullWidth
          size="small"
          multiline
          rows={4}
          value={form.description}
          onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
          error={!!errors['description']}
          helperText={errors['description'] || 'Injected into the AI signal detection prompt'}
          disabled={isPending}
          inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
        />

        <TextField
          label="Tone Guidance"
          fullWidth
          size="small"
          multiline
          rows={2}
          value={form.tone_guidance}
          onChange={(e) => setForm((p) => ({ ...p, tone_guidance: e.target.value }))}
          disabled={isPending}
          helperText="Injected into the draft reply tone block"
        />

        <TextField
          label="UI Category"
          fullWidth
          size="small"
          value={form.ui_category}
          onChange={(e) => setForm((p) => ({ ...p, ui_category: e.target.value }))}
          disabled={isPending}
          helperText="Free text grouping (e.g. buying, qualifying, objection)"
        />

        <Divider />

        <Box>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Filter Colors
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {COLOR_KEYS.map(({ key, label }) => (
              <ColorRow
                key={key}
                colorKey={key}
                label={label}
                value={form.filter_colors[key] ?? ''}
                disabled={isPending}
                onChange={handleColorChange}
              />
            ))}
          </Box>
        </Box>

        <Divider />

        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
              disabled={isPending}
            />
          }
          label="Active (sent to AI)"
        />

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>Strategies (ordered)</Typography>

        {orderedKeys.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {orderedKeys.map((key, idx) => {
              const s = getStrategy(key);
              if (!s) return null;
              return (
                <Box
                  key={key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}
                >
                  <Chip label={idx + 1} size="small" color="primary" sx={{ minWidth: 28, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{s.label}</Typography>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === 0 || isPending}
                        onClick={() => handleMoveUp(idx)}
                      >
                        <ArrowUpIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === orderedKeys.length - 1 || isPending}
                        onClick={() => handleMoveDown(idx)}
                      >
                        <ArrowDownIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={isPending}
                      onClick={() => handleRemoveStrategy(key)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}

        <Autocomplete
          size="small"
          options={availableStrategies}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.key === v.key}
          value={addStrategyInput}
          inputValue={addStrategySearch}
          onInputChange={(_, value) => setAddStrategySearch(value)}
          onChange={(_, val) => handleAddStrategy(val)}
          disabled={isPending || availableStrategies.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              label={availableStrategies.length === 0 ? 'All strategies added' : 'Add strategy…'}
              placeholder="Search and select…"
            />
          )}
        />

        {saveMutation.isError && (
          <Alert severity="error">{extractErrorMessage(saveMutation.error)}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={isPending}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          startIcon={isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignalModal;
